import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default class Timeline {
	constructor(element) {
		this.element = element;
		this.container = this.element.querySelector(".timeline");
		this.circle = this.element.querySelector(".timeline-circle");
		this.items = this.element.querySelectorAll(".timeline-item");
		this.links = this.element.querySelectorAll(".timeline-link");
		this.currentActiveIndex = 0;
		this.currentRotation = 0;

		this._cooldown = false;
		this._isPinned = false;
		this._contentTween = null;

		this._ringProgress = 0;
		this._ringDuration = 1.6;
		this._ringTicker = null;

		this.init();
		this.initScrollPin();
		this.startRingTicker();
	}

	init() {
		this.items[0].classList.add("active-el");
		this.container.style.transform = `rotate(${this.currentRotation}deg)`;

		const len = this.items.length;
		const step = 360 / len;
		for (let i = 0; i < len; i++) {
			const item = this.items[i];
			item.style.transform = `rotate(${i * step}deg)`;

			const link = item.querySelector('.timeline-link');
			const ringWrapper = document.createElement('span');
			ringWrapper.classList.add('timeline-ring-wrapper');
			const ring = document.createElement('span');
			ring.classList.add('timeline-ring');
			ringWrapper.appendChild(ring);
			link.appendChild(ringWrapper);

			gsap.set(ring, { scale: 1, opacity: 0 });

			const content = item.querySelector('.timeline-item-content');
			gsap.set(content, { opacity: i === 0 ? 1 : 0 });

			item.addEventListener("click", (event) => {
				event.preventDefault();
				this.rotate(i);
			});
		}

		this.links.forEach((link, index) => {
			link.addEventListener("focus", () => {
				if (document.activeElement === link) {
					this.rotate(index);
				}
			});
		});
	}

	startRingTicker() {
		this._ringTicker = gsap.ticker.add((time, deltaTime) => {
			this._ringProgress += deltaTime / 1000;
			if (this._ringProgress > this._ringDuration) {
				this._ringProgress -= this._ringDuration;
			}

			const t = this._ringProgress / this._ringDuration;
			const easedT = t * t;
			const scale = 1 - easedT;
			const opacity = 0.8 * (1 - easedT);

			this.items.forEach((item, i) => {
				const ring = item.querySelector('.timeline-ring');
				if (!ring) return;
				if (i === this.currentActiveIndex) {
					gsap.set(ring, { scale: 0, opacity: 0 });
				} else {
					gsap.set(ring, { scale, opacity });
				}
			});
		});
	}

	initScrollPin() {
		const totalItems = this.items.length;

		ScrollTrigger.create({
			trigger: this.element,
			start: "top top",
			end: `+=${(totalItems - 1) * 600}`,
			pin: true,
			anticipatePin: 1,
			onEnter: () => { this._isPinned = true; },
			onLeave: () => { this._isPinned = false; },
			onEnterBack: () => { this._isPinned = true; },
			onLeaveBack: () => { this._isPinned = false; },
		});

		window.addEventListener("wheel", (e) => {
			if (!this._isPinned || this._cooldown) return;

			const direction = e.deltaY > 0 ? 1 : -1;
			const next = this.currentActiveIndex + direction;

			if (next < 0 || next >= totalItems) return;

			e.preventDefault();

			this._cooldown = true;
			setTimeout(() => { this._cooldown = false; }, 550);

			this.rotate(next);

			window.scrollTo({
				top: this.element.offsetTop + next * 600,
				behavior: "instant",
			});

		}, { passive: false });
	}

	rotate(index) {
		const len = this.items.length;
		const step = 360 / len;

		const diff = this.currentActiveIndex - index;
		if (Math.abs(diff) + 2 > len / 2) {
			if (diff < 0) {
				this.currentRotation += (len - Math.abs(diff)) * step;
			} else {
				this.currentRotation -= (len - Math.abs(diff)) * step;
			}
		} else {
			this.currentRotation += diff * step;
		}

		const prevContent = this.items[this.currentActiveIndex].querySelector('.timeline-item-content');
		const nextContent = this.items[index].querySelector('.timeline-item-content');

		if (this._contentTween) this._contentTween.kill();
		this.items.forEach((item) => {
			const content = item.querySelector('.timeline-item-content');
			gsap.killTweensOf(content);
		});

		this.items.forEach((item) => {
			item.classList.remove("active-el");
			gsap.set(item.querySelector('.timeline-item-content'), { opacity: 0 });
		});

		gsap.fromTo(prevContent, { opacity: 1 }, { opacity: 0, duration: 0.18, ease: 'power1.in' });

		this.currentActiveIndex = index;
		this.container.style.transform = `rotate(${this.currentRotation}deg)`;

		this.items[index].classList.add("active-el");
		this._contentTween = gsap.fromTo(
			nextContent,
			{ opacity: 0 },
			{ opacity: 1, duration: 0.3, ease: 'power1.out', delay: 0.12 }
		);
	}
}